import React from 'react';
import { Formik } from 'formik';
import {
    TextField,
    withStyles,
    Button,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    FormHelperText,
    createMuiTheme
} from '@material-ui/core';
import * as Yup from 'yup';
import { KeyboardDatePicker } from '@material-ui/pickers';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { isSuperUser } from '../../sc-utilities';
import { ThemeProvider } from '@material-ui/styles';
import {getDateFormat} from "../../sc-utilities/date";

const defaultMaterialThemeAdmin = createMuiTheme({
    palette: {
        primary: { 500: '#0C5A99' }
    }
});

const defaultMaterialTheme = createMuiTheme({
    palette: {
        primary: { 500: '#140D8A' }
    }
});

const styles = theme => ({
    root: {
        width: `auto`,
        height: "auto",
        padding: theme.spacing(3),
    },
    rootEdit: {
        flexGrow: 1,
        paddingTop: theme.spacing(2)
    },
    submit: {
        width: '100%',
        marginTop: '10px',
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    cancel: {
        width: '120px',
        marginTop: '10px',
        marginRight: '10px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    formControl: {
        width: '100%'
    },

    datePicker: {
        width: '100%'
    }
});

const BillingAccountForm = props => {
    const {
        classes,
        formSettings,
        create,
        initialValues,
        handleSubmit,
        disabledCustomer,
        translations,
        billingModes,
        accountStatuses,
        termsOfPayment,
        customers,
        users,
        editForm,
        countryList,
        usStatesList,
        loading
    } = props;

    return (
        <div className={classes.root}>
            <Formik
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                    setSubmitting(true);
                    await handleSubmit(values);
                    setSubmitting(false);
                }}
                validationSchema={Yup.object().shape(formSettings.Validation)}
                enableReinitialize
            >
                {formikProps => {
                    const { values, touched, errors, isValid, dirty, isSubmitting, handleChange, handleBlur, handleSubmit, setFieldValue, setValues, setTouched } = formikProps;

                    const onChangeCustomer = (value) => {
                        //If delivery address is checked update fields also
                        if (values.CustomerAddress) {
                            updateAddressValues(value)
                        } else {
                            emptyFields()
                        }
                        setFieldValue("CustomerID", value)

                    }

                    const handleCheckDelivery = (e) => {
                        let addressValue = e.target.checked;
                        // If checked, populate customer address info
                        if (addressValue) {
                            updateAddressValues(values.CustomerID)
                        } else if (!addressValue) {
                            emptyFields();
                        }
                    }

                    const updateAddressValues = (customerId) => {
                        const customer = (customers ?? []).find(x => x.value === customerId)
                        const customerCountry = countryList.find((c) => c.key===customer?.Country);
                        const customerState = usStatesList.find((c) => c.key===customer?.State);

                        if (customer) {
                            setTouched({
                                ...touched,
                                "AddressLine1": true,
                                "AddressLine2": true,
                                "State": true,
                                "Country": true,
                                "City": true
                            })
                            setValues({
                                ...values,
                                "AddressLine1": customer?.AddressLine1 || "",
                                "AddressLine2": customer?.AddressLine2 || "",
                                "State": customerState || "",
                                "Country": customerCountry || "",
                                "City": customer?.City || "",
                                "CustomerAddress": true,
                            })
                        }
                    }

                    const emptyFields = () => {
                        setValues({
                            ...values,
                            "AddressLine1": "",
                            "AddressLine2": "",
                            "State": "",
                            "Country": "",
                            "City": "",
                            "CustomerAddress": false,
                        })
                    }

                    const inputProps = {
                        autoComplete: 'new-password',
                        form: {
                          autoComplete: 'off',
                        },
                      }

                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={'h6'}>{formSettings.FormTitle ? formSettings.FormTitle : null}</Typography>
                            <FormControl className={classes.formControl} margin={'dense'}>
                                <InputLabel shrink>{translations['billing_mode'] || 'Billing mode'}</InputLabel>
                                <Select disabled={editForm} name="BillingMode" margin={'dense'} value={values?.BillingMode || ''} onChange={handleChange}>
                                    {(billingModes ? [...billingModes] : []).map(option => (
                                        <MenuItem key={option.value} value={option.value || ''}>
                                            {translations[option.displayValue.toLowerCase()] || option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl className={classes.formControl} margin={'dense'}>
                                <InputLabel shrink>{translations['billing_account_status'] || 'Billing account status'}</InputLabel>
                                <Select name="Status" margin={'dense'} value={values?.Status || ''} onChange={handleChange}>
                                    {(accountStatuses ? [...accountStatuses] : []).map(option => (
                                        <MenuItem key={option.value} value={option.value || ''}>
                                            {translations[option.value] || option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <ThemeProvider theme={isSuperUser() ? defaultMaterialThemeAdmin : defaultMaterialTheme}>
                                <FormHelperText>{translations['activation_date'] || 'Activation date'}</FormHelperText>
                                <KeyboardDatePicker
                                    name={"ActivationDate"}
                                    disabled={editForm}
                                    className={classes.datePicker}
                                    clearable={true}
                                    onChange={date => setFieldValue('ActivationDate', date)}
                                    format={getDateFormat()}
                                    value={values?.ActivationDate || null}
                                    margin="dense"
                                    placeholder={translations['activation_date_text'] || 'Please select from which day account will be active (default is now)'}
                                />
                            </ThemeProvider>
                            <ThemeProvider theme={isSuperUser() ? defaultMaterialThemeAdmin : defaultMaterialTheme}>
                                <FormHelperText>{translations['expires_at'] || 'Expires at'}</FormHelperText>
                                <KeyboardDatePicker
                                    name={"ExpiresAt"}
                                    className={classes.datePicker}
                                    clearable={true}
                                    onChange={date => setFieldValue('ExpiresAt', date)}
                                    format={getDateFormat()}
                                    value={values?.ExpiresAt || null}
                                    margin="dense"
                                    placeholder={translations['expires_at_text'] || 'Please select expires date'}
                                    disablePast={true}
                                />
                            </ThemeProvider>
                            <TextField
                                disabled={editForm}
                                name="BillingDay"
                                value={values?.BillingDay || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.BillingDay && touched.BillingDay && errors.BillingDay)}
                                placeholder={translations['billing_day_text'] || 'Please select on which day account will be billed'}
                                label={translations['billing_day'] || 'Billing day'}
                                margin={'dense'}
                                fullWidth={true}
                                type="number"
                            />
                            <TextField
                                name="CustomerOrder"
                                value={values?.CustomerOrder || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.CustomerOrder && touched.CustomerOrder && errors.CustomerOrder)}
                                placeholder={translations['customer_order'] || 'Customer order'}
                                label={translations['customer_order'] || 'Customer order'}
                                margin={'dense'}
                                fullWidth={true}
                                type="text"
                                inputProps={inputProps}
                            />
                            <TextField
                                name="VAT"
                                value={values?.VAT || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.VAT && touched.VAT && errors.VAT)}
                                placeholder={translations['vat_number'] || 'VAT Number'}
                                label={translations['vat_number'] || 'VAT Number'}
                                margin={'dense'}
                                fullWidth={true}
                                type="text"
                                inputProps={inputProps}
                            />
                            <TextField
                                name="TaxClassification"
                                value={values?.TaxClassification || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.TaxClassification && touched.TaxClassification && errors.TaxClassification)}
                                placeholder={translations['tax_classification'] || 'Tax Classification'}
                                label={translations['tax_classification'] || 'Tax Classification'}
                                margin={'dense'}
                                fullWidth={true}
                                type="text"
                                inputProps={inputProps}
                            />
                            <FormControl className={classes.formControl} margin={'dense'}>
                                <InputLabel shrink>{translations['terms_of_payment_text'] || 'Please select terms of payment'}</InputLabel>
                                <Select name="TermsOfPayment" margin={'dense'} value={values?.TermsOfPayment} onChange={handleChange}>
                                    {(termsOfPayment ? [...termsOfPayment] : []).map(option => (
                                        <MenuItem key={option.value} value={option.value || ''}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {!editForm ? (
                                <FormControl className={classes.formControl} margin={'dense'}>
                                    <InputLabel shrink>{translations['customer_text'] || 'Please select customer'}</InputLabel>
                                    <Select name="CustomerID" margin={'dense'} value={values?.CustomerID || ''}
                                        disabled={disabledCustomer ? true : false}
                                        onChange={(event) => onChangeCustomer(event.target.value)}>
                                        {(customers ? [...customers] : []).map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.displayValue}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : null}
                            <FormControl style={{ width: '100%' }} disabled={props.disabled} margin="dense">
                                <Autocomplete
                                    name={'Accountant'}
                                    id="combo-box-demo"
                                    options={users ? [...users] : []}
                                    value={values?.Accountant || ''}
                                    onChange={(event, newValue) => {
                                        setFieldValue('Accountant', newValue);
                                    }}
                                    getOptionLabel={option => (typeof option.displayValue === 'string' ? option.displayValue : '')}
                                    renderInput={params => <TextField {...params} label={translations['accountant'] || 'Accountant'} margin="dense" fullWidth={true} />}
                                />
                            </FormControl>
                            <FormControl className={classes.formControl} margin={'dense'}>
                                <InputLabel shrink>{translations['please_select_language'] || 'Please select language'}</InputLabel>
                                <Select name="Language" margin={'dense'} value={values?.Language || ''} onChange={handleChange}>
                                    <MenuItem key={'en'} value={'en'}>
                                        English - {translations["english"] || `English`}
                                    </MenuItem>
                                    <MenuItem key={'de'} value={'de'}>
                                        German -  {translations["german"] || 'German'}
                                    </MenuItem>
                                    <MenuItem key={'sr'} value={'sr'}>
                                        Serbian -  {translations["serbian"] || 'Serbian'}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                name="PhoneNumber"
                                value={values?.PhoneNumber || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.PhoneNumber && touched.PhoneNumber && errors.PhoneNumber)}
                                placeholder={translations['phone_number'] || 'Phone number'}
                                label={translations['phone_number'] || 'Phone number'}
                                margin={'dense'}
                                fullWidth={true}
                                type="text"
                                inputProps={inputProps}
                            />
                            <TextField
                                name="Email"
                                value={values?.Email || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Email && touched.Email && errors.Email)}
                                placeholder={translations['e_mail'] || 'E-Mail'}
                                label={translations['e_mail'] || 'E-Mail'}
                                margin={'dense'}
                                fullWidth={true}
                                type="text"
                                inputProps={inputProps}
                            />
                            {!editForm ? (
                                <FormGroup>
                                    <FormControlLabel
                                        control={<Checkbox margin={'dense'} name={'CustomerAddress'} checked={values?.CustomerAddress || false} onChange={(e) => handleCheckDelivery(e)} />}
                                        label={translations['same_as_customer_address'] || 'Delivery address same as customer address? (Checked - Yes)'}
                                    />
                                </FormGroup>
                            ) : null}
                            <Autocomplete
                                name="Country"
                                margin={"dense"}
                                options={countryList ? [...countryList] : []}
                                value={values?.Country || ''}
                                onChange={(_event, newValue) => {
                                    if (newValue?.key !== 'US') {
                                        setValues({...values, 'Country': newValue, 'State': ''});
                                    } else {
                                        setValues({...values, 'Country': newValue, 'State': null});
                                    }
                                }}
                                getOptionLabel={option => option?.value ?? option ?? ''}
                                renderInput={params => <TextField {...params} label={translations['country'] || 'Country'} margin="dense" fullWidth={true} />}
                                disabled={values?.CustomerAddress}
                            />

                            { values?.Country && (values?.Country['key'] === 'US' || (values?.Country === 'US') || (values?.Country === 'United States of America')) ?
                                <Autocomplete
                                    name="State"
                                    margin={"dense"}
                                    options={usStatesList ? [...usStatesList] : []}
                                    value={values?.State || ''}
                                    onChange={(_event, newValue) => {
                                        setFieldValue('State', newValue);
                                    }}
                                    getOptionLabel={option => option?.value ?? option ?? ''}
                                    renderInput={params => <TextField {...params} label={translations['state'] || 'State'} margin="dense" fullWidth={true} />}
                                    disabled={values.CustomerAddress}
                                /> : null}

                            <TextField
                                name="City"
                                value={values?.City || (values?.Address ? JSON.parse(values?.Address)?.City : '')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.City && touched.City)}
                                placeholder={translations["city"] || "City"}
                                label={translations["city"] || "City"}
                                margin={"dense"}
                                fullWidth={true}
                                disabled={values?.CustomerAddress}
                                inputProps={inputProps}
                            />

                            <TextField
                                name="AddressLine1"
                                value={values?.AddressLine1 || (values?.Address ? JSON.parse(values?.Address)?.AddressLine1 : '')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.AddressLine1 && touched.AddressLine1)}
                                placeholder={translations["address_line1"] || "Address Line1"}
                                label={translations["address_line1"] || "Address Line1"}
                                margin={"dense"}
                                disabled={values?.CustomerAddress}
                                fullWidth={true}
                                inputProps={inputProps}
                            />

                            <TextField
                                name="AddressLine2"
                                value={values?.AddressLine2 || (values?.Address ? JSON.parse(values?.Address)?.AddressLine2 : '')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.AddressLine2 && touched.AddressLine2)}
                                placeholder={translations["address_line2"] || "Address Line2"}
                                label={translations["address_line2"] || "Address Line2"}
                                margin={"dense"}
                                disabled={values?.CustomerAddress}
                                fullWidth={true}
                                inputProps={inputProps}
                            />

                            {formSettings.ButtonSubmit ? (
                                <Button
                                    className={classes.submit}
                                    variant="contained"
                                    color={formSettings.ButtonSubmit.Color}
                                    type="submit"
                                    disabled={loading || !(isValid && dirty) || isSubmitting}
                                >
                                    {!create ? (
                                        loading ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            formSettings.ButtonSubmit.Title
                                        )
                                    ) : isSubmitting ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                        formSettings.ButtonSubmit.Title
                                    )}
                                </Button>
                            ) : null}
                        </form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default withStyles(styles, { withTheme: true })(BillingAccountForm);
